import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { SegmentEvent, User } from '@app/shared/interfaces';
import { Channel } from '@app/shared/models/channel';
import { ConfirmationService } from '@app/shared/services/confirmation/confirmation.service';
import { SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { SettingsService } from '@app/shared/services/settings/settings.service';
import { Observable } from 'rxjs';

function agodaPropertyValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Empty check
    if (!value) {
      return { required: true };
    }

    // Check if it's a numeric ID
    if (/^\d+$/.test(value)) {
      return value.length >= 5 && value.length <= 9 ? null : { invalidFormat: true };
    }

    // Check if it's a URL
    try {
      const url = new URL(value);
      if (url.hostname.includes('agoda.com')) {
        return null;
      }
      return { invalidUrl: true };
    } catch {
      return { invalidFormat: true };
    }
  };
}

@Component({
  standalone: false,
  selector: 'sbnb-connect-agoda-property-ids',
  templateUrl: './connect-agoda-property-ids.component.html',
  styleUrls: ['./connect-agoda-property-ids.component.scss'],
})
export class ConnectAgodaPropertyIdsComponent implements OnInit {
  email: string;
  user: User;
  loading: Boolean = false;

  @Input() propertyCount$: Observable<number>;
  @Output() stepComplete: EventEmitter<string> = new EventEmitter();
  @Output() propertiesEmitter: EventEmitter<any> = new EventEmitter();
  propertyIdsForm: UntypedFormGroup;

  constructor(
    private readonly authService: AuthenticationService,
    private readonly settingsService: SettingsService,
    private readonly segmentIoService: SegmentIoService,
    private confirmationService: ConfirmationService,
    private readonly fb: UntypedFormBuilder,
    private readonly route: ActivatedRoute
  ) {
    this.confirmationService.setConfirmation(
      'Are you sure?\nYour properties will not be connected if you exit now.',
      () => {
        return confirm('Are you sure?\nYour properties will not be connected if you exit now.');
      }
    );

    // Initialize the form with a FormArray for property IDs
    this.propertyIdsForm = this.fb.group({
      propertyIds: this.fb.array([]),
    });

    this.route.queryParams.subscribe((params) => {
      if (params.continue) {
        try {
          this.propertyIds.clear();

          this.propertyIds.push(new UntypedFormControl(params.continue));
          this.propertyIdsForm.get('propertyIds').updateValueAndValidity();
        } catch {
          // Do nothing
        }
      }
    });

    // Add the first property ID field
    this.addPropertyIdField();
  }

  ngOnInit(): void {
    this.user = this.authService.getUserDetails();
    this.email = this.user.email;
  }

  get propertyIds(): UntypedFormArray {
    return this.propertyIdsForm.get('propertyIds') as UntypedFormArray;
  }

  addPropertyIdField() {
    this.propertyIds.push(new UntypedFormControl('', [Validators.required, agodaPropertyValidator()]));
  }

  removePropertyIdField(index: number) {
    this.propertyIds.removeAt(index);
  }

  progress() {
    if (!this.email) {
      return;
    }

    if (this.propertyIdsForm.invalid) {
      return;
    }

    this.loading = true;

    this.settingsService.addAccount(this.email, null, Channel.Agoda).subscribe((res) => {
      if (res.error) {
        this.loading = false;
        return;
      }

      const propertyIds = this.propertyIds.controls.map((control) => control.value.trim()).filter((value) => value);

      for (let propertyId of propertyIds) {
        this.segmentIoService.track(SegmentEvent.AgodaPropertyIdAdded, {
          property_id: propertyId,
        });
      }

      // Continue with properties
      this.propertiesEmitter.emit(propertyIds);
    });
  }

  back() {
    window.history.back();
  }

  ngOnDestroy() {
    this.confirmationService.clearConfirmation();
  }
}
